import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { FormLayoutRoleAssociationRecord } from "@mapmycustomers/shared/types/layout/FormLayoutRoleAssociation";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";
import { Button, Modal, Popconfirm, SearchField } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import { getRolesExcludingOwners, getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { getRoleDisplayNameAlt } from "@app/util/ui";

import {
  areLayoutsRolesAssociationsSaving,
  getEditedLayoutId,
  isCreating,
  isSaving,
} from "../../store";
import { saveLayoutsRoles } from "../../store/actions";
import { NO_TEAM_ID } from "../../util/const";
import mapTeamRecord from "../../util/mapTeamRecord";

import styles from "./AsssociationsModal.module.scss";

interface Props {
  creating: boolean;
  layoutId?: FormLayout["id"];
  onHide?: () => void;
  roles: Role[];
  saveLayoutsRoles: typeof saveLayoutsRoles.request;
  savingAssociations: boolean;
  savingLayout: boolean;
  teams: Team[];
}

const messages = defineMessages({
  allRoles: {
    id: "settings.formLayouts.assignLayoutsModal.actions.allRoles",
    defaultMessage: "All Roles",
    description: "Forms Layouts - Assign Layouts modal - All Roles",
  },
  cancel: {
    id: "settings.formLayouts.assignLayoutsModal.actions.cancel",
    defaultMessage: "Assign Later",
    description: "Forms Layouts - Assign Layouts modal - Actions - Assign Later",
  },
  confirmTitle: {
    id: "settings.formLayouts.assignLayoutsModal.actions.confirmation.title",
    defaultMessage: "This layout will be immediately visible to all users of the selected role(s).",
    description: "Title of Save confirmation",
  },
  noTeam: {
    id: "settings.formLayouts.assignLayoutsModal.noTeam",
    defaultMessage: "Users without a team",
    description: "Users without a team label",
  },
  placeholder: {
    id: "settings.formLayouts.assignLayoutsModal.search",
    defaultMessage: "Search",
    description: "Search Teams placeholder",
  },
  save: {
    id: "settings.formLayouts.assignLayoutsModal.actions.save",
    defaultMessage: "Save and Assign",
    description: "Forms Layouts - Assign Layouts modal - Actions - Save and Assign",
  },
  text: {
    id: "settings.formLayouts.assignLayoutsModal.text",
    defaultMessage:
      "This layout is not currently visible to users. Assign it to roles and teams below to make it visible to reps. Layouts currently assigned will be overriden.",
    description: "Forms Layouts - Assign Layouts modal - description",
  },
  title: {
    id: "settings.formLayouts.assignLayoutsModal.title",
    defaultMessage: "Assign Layout",
    description: "Forms Layouts - Assign Layouts modal - title",
  },
  understoodBtn: {
    id: "settings.formLayouts.modal.modal.action.confirmation.understood",
    defaultMessage: "Understood",
    description: "Forms Layout Editor Modal - Understood button title",
  },
  waitBtn: {
    id: "settings.formLayouts.modal.modal.action.confirmation.wait",
    defaultMessage: "Wait",
    description: "Forms Layout Editor Modal  - Wait button title",
  },
});

type Selection = Map<Team["id"], Set<Role["id"]>>;

const AssociationsModal: React.FC<Props> = ({
  creating,
  layoutId,
  onHide,
  roles,
  saveLayoutsRoles,
  savingAssociations,
  savingLayout,
  teams,
}) => {
  const intl = useIntl();

  const [selection, setSelection] = useState<Selection>(new Map());
  const [search, setSearch] = useState<string>("");

  const allTeams: Array<Pick<Team, "id" | "name">> = useMemo(
    () => [
      {
        id: NO_TEAM_ID,
        name: intl.formatMessage(messages.noTeam),
      },
      ...teams.map(mapTeamRecord),
    ],
    [intl, teams]
  );

  const filteredTeams = useMemo(() => {
    let result = allTeams;

    if (search) {
      const searchPhrase = search.trim().toLowerCase();
      if (searchPhrase) {
        result = allTeams.filter((team) => team.name.toLowerCase().indexOf(searchPhrase) >= 0);
      }
    }

    return result.sort((a, b) =>
      a.id === NO_TEAM_ID ? -1 : b.id === NO_TEAM_ID ? 1 : a.name.localeCompare(b.name)
    );
  }, [allTeams, search]);

  const handleSearch = useCallback((value) => setSearch(value), [setSearch]);

  const handleSave = useCallback(() => {
    if (layoutId) {
      const payload: Array<FormLayoutRoleAssociationRecord> = [];

      selection.forEach((roles, teamId: Team["id"]) => {
        roles?.forEach((roleId: Role["id"]) => {
          const record: FormLayoutRoleAssociationRecord = {
            layout: {
              id: layoutId!,
            },
            role: {
              id: roleId,
            },
            team:
              teamId === NO_TEAM_ID
                ? null
                : {
                    id: teamId,
                  },
          };
          payload.push(record);
        });
      });

      saveLayoutsRoles(payload);
    }

    onHide?.();
  }, [layoutId, onHide, saveLayoutsRoles, selection]);

  const handleCheckAll = useCallback(
    (value) => {
      if (value) {
        const map = new Map();
        allTeams.forEach((team) => {
          map.set(team.id, new Set(roles.map((role) => role.id)));
        });
        setSelection(map);
      } else {
        setSelection(new Map());
      }
    },
    [allTeams, roles, setSelection]
  );

  const handleCheckTeam = useCallback(
    (id: Team["id"], value: boolean) => {
      const map = new Map(selection);
      map.delete(id);
      if (value) {
        map.set(id, new Set(roles.map((role) => role.id)));
      }
      setSelection(map);
    },
    [roles, selection, setSelection]
  );

  const handleCheckRole = useCallback(
    (id: Team["id"], roleId: Role["id"], value: boolean) => {
      const map = new Map(selection);
      if (value) {
        const set = new Set(map.get(id));
        set.add(roleId);
        map.set(id, set);
      } else {
        const set = new Set(map.get(id));
        set.delete(roleId);
        if (set.size === 0) {
          map.delete(id);
        } else {
          map.set(id, set);
        }
      }
      setSelection(map);
    },
    [selection, setSelection]
  );

  let selectedSize = 0;
  selection.forEach((roles) => (selectedSize += roles?.size ?? 0));

  const areAllSelected = selectedSize === allTeams.length * roles.length;
  const isEmptySelection = selectedSize === 0;

  return (
    <Modal
      centered
      className={styles.container}
      closable={false}
      footer={
        <>
          <Button onClick={onHide}>{intl.formatMessage(messages.cancel)}</Button>

          <Popconfirm
            cancelText={intl.formatMessage(messages.waitBtn)}
            description={intl.formatMessage(messages.confirmTitle)}
            disabled={savingLayout || creating || isEmptySelection || savingAssociations}
            okText={intl.formatMessage(messages.understoodBtn)}
            onConfirm={handleSave}
            overlayClassName={styles.saveOverlay}
            placement="topRight"
          >
            <Button
              disabled={savingLayout || creating || isEmptySelection}
              loading={savingAssociations}
              type="primary"
            >
              {intl.formatMessage(messages.save)}
            </Button>
          </Popconfirm>
        </>
      }
      mask
      maskClosable
      onCancel={onHide}
      title={intl.formatMessage(messages.title)}
      visible
      width={""}
    >
      <p>{intl.formatMessage(messages.text)}</p>
      <div className={styles.list}>
        <div className={styles.team}>
          <Checkbox checked={areAllSelected} onChange={handleCheckAll}>
            {intl.formatMessage(messages.allRoles)}
          </Checkbox>
        </div>

        <div className={styles.searchBox}>
          <SearchField
            allowClear
            onChange={handleSearch}
            placeholder={intl.formatMessage(messages.placeholder)}
          />
        </div>

        {filteredTeams.map((team) => {
          const teamSelection = selection.get(team.id);
          const isTeamSelected = teamSelection?.size === roles.length;

          return (
            <div key={team.id}>
              <div className={styles.team}>
                <Checkbox
                  checked={isTeamSelected}
                  onChange={(value) => handleCheckTeam(team.id, value)}
                >
                  {team.name}
                </Checkbox>
              </div>

              {roles.map((role: Role) => (
                <div className={styles.role} key={`${team.id}_${role.id}`}>
                  <Checkbox
                    checked={teamSelection?.has(role.id)}
                    onChange={(value) => handleCheckRole(team.id, role.id, value)}
                  >
                    {role.name ?? getRoleDisplayNameAlt(intl, role.key)}
                  </Checkbox>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  creating: isCreating(state),
  layoutId: getEditedLayoutId(state),
  roles: getRolesExcludingOwners(state),
  savingAssociations: areLayoutsRolesAssociationsSaving(state),
  savingLayout: isSaving(state),
  teams: getTeams(state),
});

const mapDispatchToProps = {
  saveLayoutsRoles: saveLayoutsRoles.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssociationsModal);
